import { ReactNode, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Container } from '@krakentech/coral';

import { AuthenticatedPageLayout, PageError } from '@/components';
import PIITypography from '@/components/PII/PIITypography';
import SuccessPage from '@/components/SuccessPage';
import { useCanSwitchToMeasuredTariff } from '@/hooks/usage/useCanSwitchToMeasuredTariff';
import { useSwitchToMeasuredPaymentSchedule } from '@/hooks/usage/useSwitchToMeasuredPaymentSchedule';
import { useDisabledFFRedirect } from '@/hooks/utils/useDisabledFFRedirect';
import { FeatureNames } from '@/types/features';
import { formatCurrency } from '@/utils/formatters/currency';
import { INTERNAL_PATHS } from '@/utils/urls';

const Success = () => {
  useDisabledFFRedirect({
    featureFlag: FeatureNames.SwitchToMeasured,
    redirectPath: INTERNAL_PATHS.HOME.path,
  });

  const { push } = useRouter();

  const { data: canSwitchToMeasured, isLoading: isLoadingCanSwitchToMeasured } =
    useCanSwitchToMeasuredTariff();

  const {
    data: switchToMeasuredPaymentSchedule,
    isLoading: isLoadingSwitchToMeasuredPaymentSchedule,
    isError: isErrorSwitchToMeasuredPaymentSchedule,
  } = useSwitchToMeasuredPaymentSchedule();

  useEffect(() => {
    if (!isLoadingCanSwitchToMeasured && !canSwitchToMeasured)
      void push(INTERNAL_PATHS.HOME.path);
  }, [isLoadingCanSwitchToMeasured, canSwitchToMeasured, push]);

  if (isLoadingSwitchToMeasuredPaymentSchedule) return null;

  if (isErrorSwitchToMeasuredPaymentSchedule) return <PageError />;

  if (!switchToMeasuredPaymentSchedule) {
    return <PageError />;
  }

  const { isVariablePaymentAmount, paymentAmount } =
    switchToMeasuredPaymentSchedule;

  return (
    <Container
      component="section"
      padding="md"
      md={{ padding: 'lg' }}
      marginX="auto"
    >
      <SuccessPage
        title="Switch complete"
        linkHref={INTERNAL_PATHS.HOME.path}
        linkTitle="Finish"
        Explanation={
          <PIITypography>
            Please allow 14 days for changes to take effect. Once completed,
            you&apos;ll start paying{' '}
            {isVariablePaymentAmount
              ? 'for the water you use which will show on'
              : `${formatCurrency(
                  paymentAmount as number
                )} every month until`}{' '}
            your first metered bill.
          </PIITypography>
        }
      />
    </Container>
  );
};

Success.getLayout = (page: ReactNode) => {
  return (
    <AuthenticatedPageLayout
      pageTitle={INTERNAL_PATHS.SWITCH_TO_MEASURED_SUCCESS.title}
    >
      {page}
    </AuthenticatedPageLayout>
  );
};

export default Success;
